// Pure derivation logic for the findings workspace — no JSX, no side effects; unit-testable independently of rendering.

// Type-only import keeps this module off the slice's runtime chain (services → auth → Amplify), like qcAttention.ts.
import type { QuestionStatusById, AssessmentQuestionStatusById, BPStatusById, EvidenceById } from '../../../app/store/questionnaireAnswerSlice';
import type { FileUpload } from '../../Forms/FileUpload';
import { Questionnaire } from '../../../interfaces/questionnaire.interface';
import { Question } from '../../../interfaces/question.interface';
import { Domain } from '../../../interfaces/domain.interface';
import { Topic } from '../../../interfaces/topic.interface';
import { BestPractice } from '../../../interfaces/bestPractice.interface';
import { Evidence, QuestionAnswer } from '../../../interfaces/questionAnswer.interface';
import { Answer } from '../../../interfaces/answer.interface';
import {
  QcCommentsByQuestionId,
  QcIssue,
  QcIssueAuthor,
  QcIssueCategory,
  QcIssueStatus,
  WorkflowQcComment,
} from '../../../interfaces/qcIssue.interface';
import { PLACEHOLDER_ANSWER_TEXTS } from '../../Admin/QC/qcAttention';

// Mirrors USER_TYPES (services/user.ts) — inlined to avoid that service's axios/Amplify chain.
const ASSESSOR_USER_TYPE = 'assessor';
const TPN_ADMIN_USER_TYPE = 'tpn_admin';

export type WorkspaceStage = 'in_progress' | 'submitted' | 'qc_submitted';
export type WorkspaceRole = 'assessor' | 'tpn_admin';
export type SliderPanel = 'sp' | 'answer' | 'qc';

export function deriveWorkspaceStage(assessmentStatus: string | undefined, qcReviewSubmitted: boolean): WorkspaceStage {
  if (assessmentStatus === 'Submitted') return qcReviewSubmitted ? 'qc_submitted' : 'submitted';
  return 'in_progress';
}

// Curated tags come off the AssessorAnswer — a single source, no mirrored slice state to drift.
export function tagsByQuestionFrom(
  assessmentQuestionsStatus: Record<number, { tags?: string[] }> | undefined,
): Record<number, string[]> {
  const byQuestion: Record<number, string[]> = {};
  Object.entries(assessmentQuestionsStatus || {}).forEach(([questionId, answer]) => {
    if (answer?.tags?.length) byQuestion[Number(questionId)] = answer.tags;
  });
  return byQuestion;
}

// The vendor answer's attachment ids resolved through the evidence cache — v1's Attachments records exactly.
export function resolveEvidenceFiles(
  attachments: number[] | undefined,
  evidenceById: EvidenceById,
): FileUpload[] {
  return (attachments || [])
    .map(id => evidenceById[id])
    .filter((ev): ev is Evidence => !!ev)
    .map(ev => ({
      id: ev.id,
      file: ev.file.document,
      date: ev.file.createdAt,
      user: ev.createdBy,
      isPublic: ev.isPublic,
    })) as FileUpload[];
}

// The redesign covers findings entry through QC review; Pre-Assessment/Remediation/Complete keep the existing UI.
export function isWorkspaceCoveredStatus(assessmentStatus: string | undefined): boolean {
  return assessmentStatus === 'Assessing' || assessmentStatus === 'Submitted';
}

export interface WorkspaceCapabilities {
  sliderDefaultPair: [SliderPanel, SliderPanel];
  qcIssuesVisible: boolean;
  assessorAnswerEditable: boolean;
  findingsEditable: boolean;
  saveFindingVisible: boolean;
  addQcIssue: boolean;
  submitQcIssues: boolean;
  markReadyForTpnReview: boolean;
  approveOrNeedsUpdate: boolean;
  firstPassVisible: boolean;
}

// Permission matrix (handoff README, amended): submitted = frozen for the assessor until QC publishes, and the freeze returns whenever their round closes — QC unfreezes only to address open issues.
export function getWorkspaceCapabilities(
  userType: string | undefined,
  stage: WorkspaceStage,
  qcReviewSubmitted: boolean,
  roundClosed = false,
  qcAssessmentApproved = false,
  allIssuesApproved = false,
): WorkspaceCapabilities {
  const isAssessor = userType === ASSESSOR_USER_TYPE;
  const isAdmin = userType === TPN_ADMIN_USER_TYPE;
  // Strict turn-taking: roundClosed = with TPN (server roundClosedAt, or all open just went Ready) — assessor fully frozen until every Ready item resolves; while open the admin resolves none.
  const assessorFrozen = stage === 'submitted' || (stage === 'qc_submitted' && roundClosed);
  return {
    sliderDefaultPair: isAssessor && stage === 'in_progress' ? ['sp', 'answer'] : ['answer', 'qc'],
    qcIssuesVisible: stage !== 'in_progress',
    assessorAnswerEditable: isAssessor && !assessorFrozen,
    findingsEditable: isAssessor && !assessorFrozen,
    saveFindingVisible: isAssessor && !assessorFrozen,
    // Admins add issues on their turn only (drafting, or round with TPN); a late add publishes immediately; all-approved or assessment approval closes the door.
    addQcIssue: isAdmin && !qcAssessmentApproved && !allIssuesApproved
      && (stage === 'submitted' || (stage === 'qc_submitted' && roundClosed)),
    submitQcIssues: isAdmin && stage === 'submitted' && !qcReviewSubmitted,
    markReadyForTpnReview: isAssessor && stage === 'qc_submitted' && !roundClosed,
    approveOrNeedsUpdate: isAdmin && stage !== 'in_progress' && roundClosed,
    firstPassVisible: isAdmin,
  };
}

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const letters = parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : parts[0].slice(0, 2);
  return letters.toUpperCase();
}

// The signed-in user as QC author; name + role must round-trip on every write, and admins act anonymously — matching qc/serializers.py _user_author so optimistic entries agree with reloads.
export function toQcAuthor(name: string, userType: string | undefined): QcIssueAuthor {
  if (userType === TPN_ADMIN_USER_TYPE) {
    return { name: 'TPN Admin', role: TPN_ADMIN_USER_TYPE, initials: 'TA' };
  }
  return { name, role: ASSESSOR_USER_TYPE, initials: getInitials(name) };
}

export function isSameQcAuthor(a: QcIssueAuthor | undefined, b: QcIssueAuthor | undefined): boolean {
  return !!a && !!b && a.name === b.name && a.role === b.role;
}

export interface OrderedQuestionEntry {
  question: Question;
  domain: Domain;
  topic: Topic;
  bestPractice: BestPractice;
}

// Flattened questionnaire order with hidden BPs/questions removed — the single source of "Question N of M" and stepping.
export function getOrderedVisibleQuestions(
  questionnaire: Questionnaire | undefined,
  bpStatus: BPStatusById,
  questionStatus: QuestionStatusById,
): OrderedQuestionEntry[] {
  if (!questionnaire) return [];
  const entries: OrderedQuestionEntry[] = [];
  questionnaire.domains.forEach(domain => {
    domain.topics.forEach(topic => {
      topic.bestPractices.forEach(bestPractice => {
        if (bpStatus[bestPractice.id]?.status === 'hide') return;
        bestPractice.questions.forEach(question => {
          if (questionStatus[question.id]?.status === 'hide') return;
          entries.push({ question, domain, topic, bestPractice });
        });
      });
    });
  });
  return entries;
}

// Complete = the assessor answer has been saved (a persisted AssessorAnswer id exists).
export function isQuestionSaved(questionId: number, assessmentQuestionsStatus: AssessmentQuestionStatusById | undefined): boolean {
  return !!assessmentQuestionsStatus?.[questionId]?.id;
}

export function getFindingsProgress(
  entries: OrderedQuestionEntry[],
  assessmentQuestionsStatus: AssessmentQuestionStatusById | undefined,
): { saved: number; total: number } {
  const saved = entries.filter(e => isQuestionSaved(e.question.id, assessmentQuestionsStatus)).length;
  return { saved, total: entries.length };
}

// The next-step banner: one short role-aware instruction pointing at whatever finishes QC fastest.
export type QcGuidanceTone = 'action' | 'waiting' | 'done';

export interface QcGuidance {
  tone: QcGuidanceTone;
  headline: string;
  detail: string;
  roundNumber?: number;
}

export function getQcGuidance(args: {
  userType: string | undefined;
  stage: WorkspaceStage;
  qcReviewSubmitted: boolean;
  qcAssessmentApproved: boolean;
  roundNumber: number;
  qcIssues: QcIssue[];
  roundClosed?: boolean;
}): QcGuidance | null {
  const { userType, stage, qcReviewSubmitted, qcAssessmentApproved, roundNumber, qcIssues, roundClosed = false } = args;
  const isAssessor = userType === ASSESSOR_USER_TYPE;
  const isAdmin = userType === TPN_ADMIN_USER_TYPE;
  if (!isAssessor && !isAdmin) return null;
  // The banner narrates QC only — during findings the workspace itself is the guidance.
  if (stage === 'in_progress') return null;

  const counted = qcIssues.filter(issue => issue.status !== 'first_pass');
  const drafts = qcIssues.length - counted.length;
  const openIssues = counted.filter(issue => ['pending_assessor_action', 'needs_update'].includes(issue.status));
  const openQuestions = new Set(openIssues.map(issue => issue.question)).size;
  const ready = counted.filter(issue => issue.status === 'ready_for_tpn_review').length;
  const allApproved = counted.length > 0 && counted.every(issue => issue.status === 'approved');
  const plural = (count: number) => (count === 1 ? '' : 's');

  // Post-approval the header badge and gauge carry the completed state — the banner retires.
  if (qcAssessmentApproved) return null;

  if (isAssessor) {
    if (!qcReviewSubmitted) {
      return {
        tone: 'waiting',
        headline: 'TPN Admin is preparing QC issues',
        detail: 'Your assessment is locked until the initial QC issues are published.',
      };
    }
    // Round with TPN: never prompt assessor action, even when mid-review edits reopen counts.
    if (roundClosed) {
      return {
        tone: 'waiting',
        roundNumber,
        headline: 'TPN Admin is reviewing this round',
        detail: 'TPN Admin is reviewing — anything returned lands here as Needs Update.',
      };
    }
    if (openIssues.length > 0) {
      return {
        tone: 'action',
        roundNumber,
        headline: `Address ${openQuestions} question${plural(openQuestions)} with open QC issues to complete Round ${roundNumber}`,
        detail: 'Rework each flagged response and mark its issue Ready for TPN Review.',
      };
    }
    if (allApproved) {
      return { tone: 'done', headline: 'All QC issues approved', detail: 'TPN Admin is finalizing this assessment.' };
    }
    return {
      tone: 'waiting',
      roundNumber,
      headline: 'All issues are with TPN',
      detail: 'TPN Admin is reviewing — anything returned lands here as Needs Update.',
    };
  }

  // TPN admin
  if (stage === 'submitted' && !qcReviewSubmitted) {
    if (drafts > 0) {
      return {
        tone: 'action',
        headline: `Review ${drafts} First Pass draft${plural(drafts)}`,
        detail: 'Accept, edit, or reject each draft, then submit QC issues to start Round 1.',
      };
    }
    return {
      tone: 'action',
      headline: 'Submit QC issues to start Round 1',
      detail: 'Publishing notifies the assessor and unlocks their workspace.',
    };
  }
  if (allApproved) {
    return {
      tone: 'action',
      headline: 'All QC issues approved — finalize from the Assessments admin',
      detail: 'Use Actions → Finalize Assessment to record the score and email the assessor.',
    };
  }
  // Ready items are decidable only once the round is with TPN — mid-round the buttons are hidden, so don't point at them.
  if (roundClosed && ready > 0) {
    return {
      tone: 'action',
      roundNumber,
      headline: `Review ${ready} issue${plural(ready)} marked Ready for TPN Review to complete Round ${roundNumber}`,
      detail: 'Approve each resolved issue or return it as Needs Update.',
    };
  }
  if (openIssues.length > 0 || ready > 0) {
    return {
      tone: 'waiting',
      roundNumber,
      headline: 'Waiting on the assessor',
      detail: openIssues.length > 0
        ? `${openIssues.length} issue${plural(openIssues.length)} pending assessor updates.`
        : 'The assessor is working through this round.',
    };
  }
  return null;
}

// Nothing Pending Assessor Action or Needs Update remains among published issues; zero published counts as closed too.
export function isAssessorRoundClosed(qcIssues: QcIssue[]): boolean {
  const counted = qcIssues.filter(issue => issue.status !== 'first_pass');
  return !counted.some(issue => ['pending_assessor_action', 'needs_update'].includes(issue.status));
}

export function getQcApprovalProgress(qcIssues: QcIssue[]): { approved: number; total: number } {
  const counted = qcIssues.filter(issue => issue.status !== 'first_pass');
  return { approved: counted.filter(issue => issue.status === 'approved').length, total: counted.length };
}

export function countQcIssuesByStatus(qcIssues: QcIssue[]): Record<QcIssueStatus, number> {
  const counts: Record<QcIssueStatus, number> = {
    first_pass: 0,
    pending_assessor_action: 0,
    ready_for_tpn_review: 0,
    approved: 0,
    needs_update: 0,
  };
  qcIssues.forEach(issue => { counts[issue.status] += 1; });
  return counts;
}

// Status tiles are filter toggles: OR within selected statuses; a question matches on any issue's status.
export function questionMatchesStatusFilters(
  questionId: number,
  statusFilters: QcIssueStatus[],
  qcIssuesByQuestion: Record<number, QcIssue[]>,
): boolean {
  if (!statusFilters.length) return true;
  const issues = qcIssuesByQuestion[questionId] || [];
  return issues.some(issue => statusFilters.includes(issue.status));
}


// Lifecycle display order for grouped cards — Needs Update outranks plain Pending (rework surfaces first).
export const QC_GROUP_ORDER: QcIssueStatus[] = [
  'first_pass',
  'needs_update',
  'pending_assessor_action',
  'ready_for_tpn_review',
  'approved',
];

// "Scopes N question(s)": how many other questions' visibility this question's answer options gate.
export function getScopedQuestionCount(entries: OrderedQuestionEntry[], question: Question): number {
  const answerIds = new Set(question.answers.map(a => a.id));
  let count = 0;
  entries.forEach(entry => {
    if (entry.question.id === question.id) return;
    const gated = (entry.question.inclusions || []).some(config => {
      const answerId = typeof config.answer === 'number' ? config.answer : config.answer?.id;
      return answerId !== undefined && answerIds.has(answerId);
    });
    if (gated) count += 1;
  });
  return count;
}

export interface NavigatorQuestionRow {
  entry: OrderedQuestionEntry;
  qNum: number;
}

export interface NavigatorBpGroup {
  bestPractice: BestPractice;
  shortCode: string;
  bpNumber?: string;
  rows: NavigatorQuestionRow[];
}

export interface NavigatorTopicGroup {
  topic: Topic;
  topicNumber?: string;
  bps: NavigatorBpGroup[];
}

// Question codes encode position: OR.4.0.1 (or OR-4.0.1) = domain OR, topic 4, BP 0, question 1.
export function parseQuestionCode(shortCode?: string): {
  domainCode?: string;
  topicNumber?: string;
  bpNumber?: string;
} {
  const parts = (shortCode || '').split(/[.\-\s]+/).filter(Boolean);
  if (parts.length < 3) return {};
  return { domainCode: parts[0], topicNumber: parts[1], bpNumber: parts[2] };
}

export interface NavigatorDomainGroup {
  domain: Domain;
  topics: NavigatorTopicGroup[];
}

// Navigator tree: entries regrouped domain → topic → BP, with the question's ordinal (the Q{n} label).
export function groupEntriesForNavigator(entries: OrderedQuestionEntry[]): NavigatorDomainGroup[] {
  const domains: NavigatorDomainGroup[] = [];
  entries.forEach(entry => {
    let domainGroup = domains.find(d => d.domain.id === entry.domain.id);
    if (!domainGroup) {
      domainGroup = { domain: entry.domain, topics: [] };
      domains.push(domainGroup);
    }
    const code = parseQuestionCode(entry.question.shortCode);
    let topicGroup = domainGroup.topics.find(t => t.topic.id === entry.topic.id);
    if (!topicGroup) {
      topicGroup = { topic: entry.topic, topicNumber: code.topicNumber, bps: [] };
      domainGroup.topics.push(topicGroup);
    }
    let bpGroup = topicGroup.bps.find(b => b.bestPractice.id === entry.bestPractice.id);
    if (!bpGroup) {
      const fullCode = code.domainCode && code.topicNumber && code.bpNumber
        ? `${code.domainCode}-${code.topicNumber}.${code.bpNumber}`
        : undefined;
      bpGroup = {
        bestPractice: entry.bestPractice,
        shortCode: fullCode || entry.bestPractice.title,
        bpNumber: code.bpNumber,
        rows: [],
      };
      topicGroup.bps.push(bpGroup);
    }
    bpGroup.rows.push({ entry, qNum: bpGroup.rows.length + 1 });
  });
  return domains;
}

// Case-insensitive substring over the full hierarchy — everything the search placeholder promises.
export function navigatorSearchMatch(row: NavigatorQuestionRow, search: string): boolean {
  const sq = search.trim().toLowerCase();
  if (!sq) return true;
  const haystack = `${row.entry.domain.title} ${row.entry.topic.title} ${row.entry.question.shortCode || ''} `
    + `${row.entry.bestPractice.title} ${row.entry.question.title} Q${row.qNum}`;
  return haystack.toLowerCase().indexOf(sq) >= 0;
}

// Dirty signature (prototype sig()): answer + findings + sorted tags; Save enables only when it differs from the last saved.
export function findingSignature(answer: string | undefined, findings: string | undefined, tags: string[]): string {
  return JSON.stringify([answer ?? null, findings ?? null, [...tags].sort()]);
}

export type SaveStatusLine =
  | { kind: 'not_saved' }
  | { kind: 'saved'; by: string; at: number }
  | { kind: 'saved_previously' }
  | { kind: 'unsaved_changes' };

export function getSaveStatusLine(
  dirty: boolean,
  sessionSave: { by: string; at: number } | undefined,
  persisted: boolean,
): SaveStatusLine {
  if (dirty && (sessionSave || persisted)) return { kind: 'unsaved_changes' };
  if (sessionSave) return { kind: 'saved', by: sessionSave.by, at: sessionSave.at };
  if (persisted) return { kind: 'saved_previously' };
  return { kind: 'not_saved' };
}

export function formatSavedTime(ts: number): string {
  const d = new Date(ts);
  const hours = ((d.getHours() + 11) % 12) + 1;
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes} ${d.getHours() < 12 ? 'AM' : 'PM'}`;
}

// ─── Question links (`#CODE`) ─────────────────────────────────────────────────

// A trailing `#…` opens the question-link dropdown; null when the caret is not in a hash token.
export function getMentionTerm(text: string): string | null {
  const match = /#([A-Za-z0-9.\-]*)$/.exec(text);
  return match ? match[1] : null;
}

export function getMentionMatches(
  entries: OrderedQuestionEntry[],
  term: string,
  limit: number,
): OrderedQuestionEntry[] {
  const needle = term.toLowerCase();
  const seen = new Set<string>();
  const matches: OrderedQuestionEntry[] = [];
  entries.forEach(entry => {
    const code = entry.question.shortCode;
    if (!code || seen.has(code) || matches.length >= limit) return;
    if (needle && code.toLowerCase().indexOf(needle) < 0 && (entry.question.title || '').toLowerCase().indexOf(needle) < 0) return;
    seen.add(code);
    matches.push(entry);
  });
  return matches;
}

export function applyMention(text: string, shortCode: string): string {
  return text.replace(/#[A-Za-z0-9.\-]*$/, `#${shortCode} `);
}

export interface TextPart {
  text: string;
  shortCode?: string;
}

// Splits text into plain segments and `#CODE` links; unmatched hash tokens stay plain text.
export function splitQuestionLinks(text: string, entries: OrderedQuestionEntry[]): TextPart[] {
  const codes = new Map<string, string>();
  entries.forEach(entry => {
    if (entry.question.shortCode) codes.set(entry.question.shortCode.toLowerCase(), entry.question.shortCode);
  });
  const parts: TextPart[] = [];
  const pattern = /#([A-Za-z]{2,4}[-.][\d.]*\d)/g;
  let last = 0;
  let match = pattern.exec(text);
  while (match !== null) {
    if (match.index > last) parts.push({ text: text.slice(last, match.index) });
    const shortCode = codes.get(match[1].toLowerCase());
    parts.push(shortCode ? { text: `#${shortCode}`, shortCode } : { text: match[0] });
    last = match.index + match[0].length;
    match = pattern.exec(text);
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts.length ? parts : [{ text }];
}

// ─── Assessment-wide QC roll-ups ─────────────────────────────────────────────

export function countUnaddressedFirstPass(qcIssuesByQuestion: Record<number, QcIssue[]>): number {
  return Object.values(qcIssuesByQuestion)
    .reduce((total, issues) => total + issues.filter(issue => issue.status === 'first_pass').length, 0);
}

export interface QcSummaryRow {
  entry: OrderedQuestionEntry;
  issue: QcIssue;
}

// Assessment-wide issue list in questionnaire order (submission + approval modals); First Pass drafts never appear.
export function getQcSummaryRows(
  entries: OrderedQuestionEntry[],
  qcIssuesByQuestion: Record<number, QcIssue[]>,
): QcSummaryRow[] {
  const rows: QcSummaryRow[] = [];
  entries.forEach(entry => {
    (qcIssuesByQuestion[entry.question.id] || []).forEach(issue => {
      if (issue.status === 'first_pass') return;
      rows.push({ entry, issue });
    });
  });
  return rows;
}

export function countQcCategories(rows: QcSummaryRow[]): { category: QcIssueCategory; count: number }[] {
  const counts = new Map<QcIssueCategory, number>();
  rows.forEach(({ issue }) => counts.set(issue.category, (counts.get(issue.category) || 0) + 1));
  return Array.from(counts.entries()).map(([category, count]) => ({ category, count }));
}

export type SpBucket = 'implemented' | 'compensating_control' | 'not_applicable' | 'not_implemented' | 'unanswered';

export interface SpGroupedControls {
  bucket: SpBucket;
  controls: Answer[];
}

// The vendor's aggregate call lives in question.answers beside the real sub-controls; show it as overall, group only real ones.
export function getSpOverallAnswer(question: Question, questionAnswer: QuestionAnswer | undefined): string | undefined {
  if (!questionAnswer?.answer?.length) return undefined;
  const aggregate = question.answers.find(a =>
    questionAnswer.answer!.includes(a.id) && PLACEHOLDER_ANSWER_TEXTS.includes(a.text || ''));
  return aggregate?.text;
}


export function groupSpControls(question: Question, questionAnswer: QuestionAnswer | undefined): SpGroupedControls[] {
  const buckets: Record<SpBucket, Answer[]> = {
    implemented: [],
    compensating_control: [],
    not_applicable: [],
    not_implemented: [],
    unanswered: [],
  };
  question.answers.forEach(answer => {
    if (PLACEHOLDER_ANSWER_TEXTS.includes(answer.text || '')) return;
    if (questionAnswer?.answer?.includes(answer.id)) buckets.implemented.push(answer);
    else if (questionAnswer?.compensatingControlAnswers?.includes(answer.id)) buckets.compensating_control.push(answer);
    else if (questionAnswer?.nonApplicableAnswers?.includes(answer.id)) buckets.not_applicable.push(answer);
    else if (questionAnswer?.nonImplementedAnswers?.includes(answer.id)) buckets.not_implemented.push(answer);
    else buckets.unanswered.push(answer);
  });
  return (Object.keys(buckets) as SpBucket[])
    .map(bucket => ({ bucket, controls: buckets[bucket] }))
    .filter(group => group.controls.length > 0);
}

// Overall SP label: the per-control blend wins whenever controls are answered (the stored aggregate can lag them); the placeholder stands in only when none carry a disposition.
export function getSpOverallLabel(question: Question, questionAnswer: QuestionAnswer | undefined): string {
  const groups = groupSpControls(question, questionAnswer);
  const answered = groups.filter(g => g.bucket !== 'unanswered');
  if (!answered.length) return getSpOverallAnswer(question, questionAnswer) ?? 'Not Answered';
  if (answered.length === 1 && answered[0].bucket === 'implemented') return 'Fully Implemented';
  if (answered.length === 1 && answered[0].bucket === 'not_implemented') return 'Not Implemented';
  if (answered.length === 1 && answered[0].bucket === 'not_applicable') return 'Not Applicable';
  return 'Partially Implemented';
}

// Groups the flat issues response into per-question shape; rejected drafts are a terminal training artifact — drop them here ("leaves no trace").
export function groupQcIssuesByQuestion(issues: QcIssue[]): Record<number, QcIssue[]> {
  return issues.reduce((acc, issue) => {
    if ((issue.status as string) === 'rejected') return acc;
    (acc[issue.question] = acc[issue.question] || []).push(issue);
    return acc;
  }, {} as Record<number, QcIssue[]>);
}

// Groups the flat comments response into the per-question shape the slice stores.
export function groupQcCommentsByQuestion(comments: WorkflowQcComment[]): QcCommentsByQuestionId {
  return comments.reduce((acc, comment) => {
    (acc[comment.question] = acc[comment.question] || []).push(comment);
    return acc;
  }, {} as QcCommentsByQuestionId);
}

// Selectable overall-answer labels shared by the SP and Assessor filter families.
export const ANSWER_FILTER_OPTIONS = ['Fully Implemented', 'Partially Implemented', 'Not Implemented', 'Not Applicable'];

export interface WorkspaceFilters {
  statusFilters: QcIssueStatus[];
  tagFilters: string[];
  spAnswerFilters: string[];
  assessorAnswerFilters: string[];
  qcOnlyFilter: boolean;
  incompleteOnlyFilter: boolean;
}

export interface WorkspaceFilterContext {
  qcIssuesByQuestion: Record<number, QcIssue[]>;
  tagsByQuestion: Record<number, string[]>;
  assessmentQuestionsStatus: AssessmentQuestionStatusById | undefined;
  questionStatus: QuestionStatusById | undefined;
}

export function hasActiveFilters(filters: WorkspaceFilters): boolean {
  return !!filters.statusFilters.length || !!filters.tagFilters.length
    || !!filters.spAnswerFilters.length || !!filters.assessorAnswerFilters.length
    || filters.qcOnlyFilter || filters.incompleteOnlyFilter;
}

// Prototype navMatchFor: OR within each family, AND across families.
export function questionMatchesFilters(
  question: Question,
  filters: WorkspaceFilters,
  ctx: WorkspaceFilterContext,
): boolean {
  const questionId = question.id;
  if (filters.tagFilters.length) {
    const tags = ctx.tagsByQuestion[questionId] || [];
    if (!filters.tagFilters.some(tag => tags.includes(tag))) return false;
  }
  if (filters.spAnswerFilters.length) {
    const spLabel = getSpOverallLabel(question, ctx.questionStatus?.[questionId]?.questionAnswer);
    if (!filters.spAnswerFilters.includes(spLabel)) return false;
  }
  if (filters.assessorAnswerFilters.length) {
    const assessorLabel = ctx.assessmentQuestionsStatus?.[questionId]?.answer;
    if (!assessorLabel || !filters.assessorAnswerFilters.includes(assessorLabel)) return false;
  }
  if (filters.qcOnlyFilter && !(ctx.qcIssuesByQuestion[questionId] || []).length) return false;
  if (filters.incompleteOnlyFilter && isQuestionSaved(questionId, ctx.assessmentQuestionsStatus)) return false;
  return questionMatchesStatusFilters(questionId, filters.statusFilters, ctx.qcIssuesByQuestion);
}

export function getFilteredEntries(
  entries: OrderedQuestionEntry[],
  filters: WorkspaceFilters,
  ctx: WorkspaceFilterContext,
): OrderedQuestionEntry[] {
  if (!hasActiveFilters(filters)) return entries;
  return entries.filter(e => questionMatchesFilters(e.question, filters, ctx));
}
